import React from 'react';
import { useUmami, UmamiEvents } from './hooks/useUmami';
import { domainUrl, ROUTES } from './domainConfig';
import { useTranslation } from './i18n';

function Logo() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      <div className="relative h-9 w-20 rounded-full border-2 border-[#2d604d] bg-gradient-to-r from-[#3f7f65] via-[#26443f] to-[#7a3f98] shadow-[0_0_0_4px_rgba(63,127,101,.18)]">
        <div className="absolute right-1 top-1 h-7 w-7 rounded-full bg-[#8b42a8] shadow-[inset_6px_7px_0_rgba(255,255,255,.16),0_0_0_2px_rgba(57,32,72,.5)]" />
      </div>
      <div className="leading-none">
        <div className="text-lg font-black tracking-tight text-[#151827] md:text-xl">Betaaldsolliciteren.nl</div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#151827]/60 md:text-xs">{t('auth.brand_tagline')}</div>
      </div>
    </div>
  );
}

function TornLabel({ children, className = '', bgColor = '#ffffff', textColor = '#151827' }) {
  return (
    <span
      className={`inline-block px-4 py-2 text-sm font-black uppercase tracking-tight shadow-xl sm:text-base ${className}`}
      style={{
        clipPath: 'polygon(0 13%, 94% 0, 100% 82%, 7% 100%)',
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      {children}
    </span>
  );
}

export default function LandingPage({ onNavigate }) {
  const { trackEvent } = useUmami();
  const { t } = useTranslation();
  const noInternshipLine = t('landing.proof_section.no_internship_line');
  const proofFooterLine = t('landing.proof.footer');

  const steps = [
    {
      title: t('landing.steps.step_1.title'),
      copy: t('landing.steps.step_1.copy'),
      number: '01',
    },
    {
      title: t('landing.steps.step_2.title'),
      copy: t('landing.steps.step_2.copy'),
      number: '02',
    },
    {
      title: t('landing.steps.step_3.title'),
      copy: t('landing.steps.step_3.copy'),
      number: '03',
    },
    {
      title: t('landing.steps.step_4.title'),
      copy: t('landing.steps.step_4.copy'),
      number: '04',
    },
  ];

  const talentUrl = ROUTES.kandidaatPortaal;
  const bedrijvenUrl = domainUrl('bedrijven', ROUTES.bedrijvenLanding);
  const werepresentUrl = domainUrl('werepresent', ROUTES.werepresentLanding);
  const privacyUrl = domainUrl('werepresent', ROUTES.privacy);
  const voorwaardenUrl = domainUrl('werepresent', ROUTES.voorwaarden);
  const aiPolicyUrl = domainUrl('werepresent', ROUTES.aiPolicy);
  const cookieUrl = domainUrl('werepresent', ROUTES.cookieverklaring);

  const handleCTAClick = (path, location) => {
    trackEvent(UmamiEvents.CTA_CLICKED, { button: path, location });
    onNavigate(path);
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f6f1e8] text-[#151827]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />
      <div className="pointer-events-none absolute -left-24 -top-32 h-96 w-96 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
      <div className="pointer-events-none absolute -right-28 top-10 h-[34rem] w-[34rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />

      <header className="relative z-20 mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-6 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 text-sm font-bold uppercase tracking-wider text-[#151827]/75 md:flex">
          <button onClick={() => document.getElementById('hoe-het-werkt')?.scrollIntoView({ behavior: 'smooth' })}>{t('landing.nav.how_it_works')}</button>
          <button onClick={() => document.getElementById('bewijs')?.scrollIntoView({ behavior: 'smooth' })}>{t('landing.nav.proof_hours')}</button>
          <button onClick={() => handleCTAClick(talentUrl, 'header-inline')}>{t('landing.nav.for_talent')}</button>
        </nav>
        <button
          onClick={() => handleCTAClick(talentUrl, 'header-primary')}
          className="rounded-full bg-[#151827] px-5 py-3 text-sm font-black text-white shadow-xl transition hover:-translate-y-0.5 sm:px-6"
        >
          {t('landing.nav.start_match')}
        </button>
      </header>

      <section className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 pb-16 pt-6 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:pb-20 lg:pt-10">
        <div className="animate-dada-in">
          <div className="mb-7 flex flex-wrap gap-3">
            <TornLabel className="-rotate-2">{t('landing.hero.labels.no_cvs')}</TornLabel>
            <TornLabel className="rotate-1" bgColor="#713d8d" textColor="#ffffff">{t('landing.hero.labels.no_assumptions')}</TornLabel>
            <TornLabel className="-rotate-1" bgColor="#3f7f65" textColor="#ffffff">{t('landing.hero.labels.proof_100_hours')}</TornLabel>
          </div>

          <h1 className="relative max-w-5xl text-[clamp(2.8rem,10vw,8.2rem)] font-black leading-[0.88] tracking-[-0.05em]">
            {t('landing.hero.title_prefix')} <span className="inline-block text-[#3f7f65]">{t('landing.hero.title_brand')}</span>
            <span className="text-[#3f7f65]">?</span>
            <span className="absolute -right-2 top-4 hidden rotate-12 rounded-full border-4 border-[#151827] px-4 py-2 text-base tracking-normal lg:block">
              {t('landing.hero.proof_hours_bubble')}
            </span>
          </h1>

          <p className="mt-7 max-w-2xl text-xl font-semibold leading-snug text-[#2a2f40] sm:text-2xl">
            {t('landing.hero.subtitle')}
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <button
              onClick={() => handleCTAClick(talentUrl, 'hero-primary')}
              className="group rounded-2xl bg-[#3f7f65] px-8 py-5 text-left text-xl font-black text-white transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '10px 10px 0 #151827' }}
            >
              {t('landing.hero.primary_cta')}
            </button>
            <button
              onClick={() => document.getElementById('hoe-het-werkt')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-2xl border-4 border-[#151827] bg-white px-8 py-5 text-xl font-black transition hover:translate-x-1 hover:translate-y-1"
              style={{ boxShadow: '8px 8px 0 #713d8d' }}
            >
              {t('landing.hero.secondary_cta')}
            </button>
          </div>
        </div>

        <div className="relative animate-dada-in-delayed">
          <div className="absolute -left-8 top-16 z-0 h-32 w-32 rounded-full bg-[#713d8d] opacity-80 blur-sm" />
          <div className="absolute -right-4 bottom-20 z-0 h-40 w-40 rounded-full bg-[#3f7f65] opacity-70 blur-sm" />

          <div className="relative z-10 rotate-1 rounded-[2rem] border-4 border-[#151827] bg-white p-6" style={{ boxShadow: '18px 18px 0 #151827' }}>
            <div className="mb-5 flex items-center justify-between">
              <div className="rounded-full bg-[#151827] px-4 py-2 text-xs font-black uppercase tracking-widest text-white sm:text-sm">{t('landing.hero_card.label')}</div>
              <span className="text-2xl">✦</span>
            </div>

            <div className="grid gap-4" id="bewijs">
              <div className="rounded-3xl border-2 border-[#151827]/15 bg-[#f6f1e8] p-5">
                <div className="mb-2 text-lg font-black">{t('landing.hero_card.hours_title')}</div>
                <div className="h-3 overflow-hidden rounded-full bg-[#151827]/10">
                  <div className="h-full w-[78%] rounded-full bg-gradient-to-r from-[#3f7f65] to-[#713d8d]" />
                </div>
              </div>
              <div className="rounded-3xl bg-[#151827] p-5 text-white">
                <div className="mb-1 text-2xl font-black">{t('landing.hero_card.payment_value')}</div>
                <p className="text-white/75">{t('landing.hero_card.payment_body')}</p>
              </div>
              <div className="-rotate-2 rounded-3xl border-2 border-[#151827] bg-[#713d8d] p-5 text-white">
                <div className="text-xl font-black">{t('landing.hero_card.accent_box')}</div>
              </div>
              <div className="flex items-center gap-4 rounded-3xl border-2 border-dashed border-[#151827] bg-white p-5">
                <div className="text-3xl text-[#3f7f65]">⌁</div>
                <div>
                  <div className="font-black uppercase">{t('landing.hero_card.no_internship')}</div>
                  <div className="font-black uppercase text-xs">{t('landing.hero_card.card_label')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 overflow-hidden border-y-4 border-[#151827] bg-[#151827] py-5 text-white">
        <div className="whitespace-nowrap text-2xl font-black uppercase tracking-tight sm:text-3xl animate-marquee">
          {t('landing.marquee')}
        </div>
      </section>

      <section className="relative z-10 bg-[#151827] py-20 text-white md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-12 text-2xl font-bold text-gray-200 md:text-3xl">
            {t('landing.problem.title_prefix')} <span className="text-white">{t('landing.problem.title_accent')}</span>
          </h2>

          <div className="mb-12 space-y-2 text-lg text-gray-300 md:text-xl">
            <p>{t('landing.problem.lines.line_1')}</p>
            <p>{t('landing.problem.lines.line_2')}</p>
            <p>{t('landing.problem.lines.line_3')}</p>
            <p className="font-medium text-white">{t('landing.problem.lines.line_4')}</p>
          </div>

          <p className="text-2xl font-black text-[#8fd6b6] md:text-3xl">{t('landing.problem.outro')}</p>

          <div className="mt-10 flex justify-center">
            <button
              onClick={() => handleCTAClick(talentUrl, 'problem')}
              className="rounded-2xl bg-[#3f7f65] px-8 py-4 text-lg font-black text-white"
              style={{ boxShadow: '8px 8px 0 #713d8d' }}
            >
              {t('landing.problem.cta')}
            </button>
          </div>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-16 text-center">
            <p className="text-xl leading-relaxed text-[#2a2f40] md:text-2xl">
              {t('landing.proof.body_1')} <em>{t('landing.proof.say_can')}</em>.
              <br />
              <span className="font-bold text-[#151827]">{t('landing.proof.body_2')}</span>
            </p>
          </div>

          <div className="mb-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="rounded-[1.5rem] border-4 border-[#151827] bg-white p-8 text-center" style={{ boxShadow: '8px 8px 0 #3f7f65' }}>
              <div className="mb-2 text-5xl font-black text-[#3f7f65]">100</div>
              <div className="text-lg font-medium text-[#2a2f40]">{t('landing.proof_section.hours')}</div>
            </div>
            <div className="rounded-[1.5rem] border-4 border-[#151827] bg-white p-8 text-center" style={{ boxShadow: '8px 8px 0 #713d8d' }}>
              <div className="mb-2 text-5xl font-black text-[#713d8d]">€ 25</div>
              <div className="text-lg font-medium text-[#2a2f40]">{t('landing.proof_section.per_hour')}</div>
            </div>
            <div className="rounded-[1.5rem] border-4 border-[#151827] bg-white p-8 text-center" style={{ boxShadow: '8px 8px 0 #151827' }}>
              <div className="mb-2 text-5xl font-black text-[#151827]">{t('landing.proof_section.real_assignment')}</div>
              <div className="text-lg font-medium text-[#2a2f40]">{t('landing.proof_section.assignment_label')}</div>
            </div>
          </div>

          <p className="text-center text-lg text-[#2a2f40]/70">{noInternshipLine}</p>
          {proofFooterLine !== noInternshipLine && (
            <p className="text-center text-lg text-[#2a2f40]/70">{proofFooterLine}</p>
          )}
        </div>
      </section>

      <section className="relative z-10 bg-white/60 py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="mb-10 space-y-4 text-lg text-[#2a2f40]/80 md:text-xl">
            <p>{t('landing.why_old.lines.line_1')}</p>
            <p>{t('landing.why_old.lines.line_2')}</p>
            <p>{t('landing.why_old.lines.line_3')}</p>
          </div>

          <p className="mb-4 text-2xl font-black text-[#151827] md:text-3xl">{t('landing.why_old.not_in_text')}</p>
          <p className="mb-12 text-2xl font-black text-[#3f7f65] md:text-3xl">{t('landing.why_old.in_action')}</p>

          <p className="text-xl text-[#2a2f40]">{t('landing.why_old.body')}</p>
          <p className="mt-4 text-2xl font-black text-[#713d8d]">{t('landing.why_old.outro')}</p>
        </div>
      </section>

      <section className="relative z-10 bg-[#3f7f65] py-20 text-white md:py-28">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-black md:text-5xl">{t('landing.stop.title_1')}</h2>
            <h2 className="text-3xl font-black md:text-5xl">{t('landing.stop.title_2')}</h2>
          </div>

          <div className="mx-auto max-w-2xl text-center">
            <p className="mb-10 text-lg text-green-100 md:text-xl">
              {t('landing.stop.body_1')}
              <br />
              <span className="font-semibold text-white">{t('landing.stop.body_2')}</span>
            </p>

            <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-xl border-2 border-white/30 bg-white/10 p-6">
                <p className="mb-3 text-green-200 line-through">{t('landing.stop.negatives.item_1')}</p>
                <p className="mb-3 text-green-200 line-through">{t('landing.stop.negatives.item_2')}</p>
                <p className="text-green-200 line-through">{t('landing.stop.negatives.item_3')}</p>
              </div>
              <div className="rounded-xl border-2 border-white/40 bg-white/20 p-6">
                <p className="mb-3 font-semibold">{t('landing.stop_section.hours_line')}</p>
                <p className="mb-3 font-semibold">{t('landing.stop_section.pay_line')}</p>
                <p className="mb-3 font-semibold">{t('landing.stop_section.task_line')}</p>
                <p className="font-semibold">{t('landing.stop_section.responsibility_line')}</p>
              </div>
            </div>

            <p className="text-xl font-black md:text-2xl">{t('landing.stop.outro')}</p>
          </div>
        </div>
      </section>

      <section id="hoe-het-werkt" className="relative z-10 mx-auto max-w-7xl px-5 py-16 sm:px-8 md:py-24">
        <h2 className="mb-12 text-center text-3xl font-black md:text-4xl">{t('landing.how_it_works.title')}</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {steps.map((step, index) => {
          const isMiddle = index === 1;
          return (
            <article
              key={step.number}
              className="rounded-[2rem] border-4 border-[#151827] bg-white p-7 transition hover:-translate-y-1"
              style={{ boxShadow: isMiddle ? '10px 10px 0 #713d8d' : '10px 10px 0 #3f7f65' }}
            >
              <div className="mb-5 text-5xl font-black text-[#713d8d]">{step.number}</div>
              <h3 className="mb-3 text-2xl font-black">{step.title}</h3>
              <p className="text-lg font-medium text-[#2a2f40]">{step.copy}</p>
            </article>
          );
        })}
        </div>
      </section>

      <section className="relative z-10 bg-[#151827] py-20 text-white md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-12 text-3xl font-black md:text-4xl">{t('landing.why_different.title')}</h2>

          <div className="space-y-6 text-lg text-gray-200 md:text-xl">
            <p>{t('landing.why_different.line_1_prefix')} <span className="font-semibold text-white">{t('landing.why_different.line_1_accent')}</span> {t('landing.why_different.line_1_suffix')}</p>
            <p>{t('landing.why_different.line_2')}</p>
            <p>{t('landing.why_different.line_3_prefix')} <span className="font-semibold text-[#8fd6b6]">{t('landing.why_different.line_3_accent')}</span> {t('landing.why_different.line_3_suffix')}</p>
            <p>{t('landing.why_different.line_4_prefix')} <span className="font-semibold text-white">{t('landing.why_different.line_4_accent')}</span>.</p>
          </div>

          <div className="mt-12 rounded-[1.5rem] border-2 border-white/25 bg-white/10 p-8">
            <p className="mb-2 text-xl font-black text-[#8fd6b6] md:text-2xl">{t('landing.why_different.outro_title')}</p>
            <p className="text-lg text-gray-100">{t('landing.why_different.outro_body_prefix')} <span className="font-semibold text-white">{t('landing.why_different.outro_body_accent')}</span></p>
          </div>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="mb-16 text-center text-3xl font-black md:text-4xl">{t('landing.audience.title')}</h2>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="rounded-[2rem] border-4 border-[#151827] bg-white p-8" style={{ boxShadow: '10px 10px 0 #3f7f65' }}>
              <h3 className="mb-6 text-2xl font-black text-[#3f7f65]">{t('landing.audience.talent_title')}</h3>
              <ul className="space-y-4 text-[#2a2f40]">
                <li className="flex items-start gap-3"><span className="font-black text-[#3f7f65]">✓</span><span>{t('landing.audience.talent_items.item_1')}</span></li>
                <li className="flex items-start gap-3"><span className="font-black text-[#3f7f65]">✓</span><span>{t('landing.audience.talent_items.item_2')}</span></li>
                <li className="flex items-start gap-3"><span className="font-black text-[#3f7f65]">✓</span><span>{t('landing.audience.talent_items.item_3')}</span></li>
                <li className="flex items-start gap-3"><span className="font-black text-[#3f7f65]">✓</span><span>{t('landing.audience.talent_items.item_4')}</span></li>
                <li className="flex items-start gap-3"><span className="font-black text-[#3f7f65]">✓</span><span>{t('landing.audience.talent_items.item_5')}</span></li>
              </ul>
              <button
                onClick={() => handleCTAClick(talentUrl, 'for-who-talent')}
                className="mt-8 w-full rounded-xl bg-[#3f7f65] py-3 font-black text-white"
              >
                {t('landing.audience.talent_cta')}
              </button>
            </div>

            <div className="rounded-[2rem] border-4 border-[#151827] bg-white p-8" style={{ boxShadow: '10px 10px 0 #713d8d' }}>
              <h3 className="mb-6 text-2xl font-black text-[#713d8d]">{t('landing.audience.company_title')}</h3>
              <p className="mb-4 text-[#2a2f40]">{t('landing.audience.company_body')}</p>
              <p className="mb-6 text-sm italic text-[#2a2f40]/70">{t('landing.audience.company_tagline')}</p>
              <button
                onClick={() => onNavigate(bedrijvenUrl)}
                className="mt-4 w-full rounded-xl bg-[#713d8d] py-3 font-black text-white"
              >
                {t('landing.audience.company_cta')}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-white/60 py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-12 text-3xl font-black text-[#151827] md:text-4xl">{t('landing.promise.title')}</h2>

          <div className="mb-12 space-y-4 text-xl text-[#2a2f40]/70 md:text-2xl">
            <p>{t('landing.promise.items.item_1')}</p>
            <p>{t('landing.promise.items.item_2')}</p>
            <p>{t('landing.promise.items.item_3')}</p>
          </div>

          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <div className="rounded-xl border-4 border-[#151827] bg-white p-6" style={{ boxShadow: '6px 6px 0 #3f7f65' }}><div className="text-2xl font-black text-[#3f7f65]">100</div><div className="mt-1 text-sm text-[#2a2f40]/70">{t('landing.promise.cards.card_1')}</div></div>
            <div className="rounded-xl border-4 border-[#151827] bg-white p-6" style={{ boxShadow: '6px 6px 0 #151827' }}><div className="text-2xl font-black text-[#151827]">{t('landing.promise.cards.card_2_title')}</div><div className="mt-1 text-sm text-[#2a2f40]/70">{t('landing.promise.cards.card_2_body')}</div></div>
            <div className="rounded-xl border-4 border-[#151827] bg-white p-6" style={{ boxShadow: '6px 6px 0 #151827' }}><div className="text-2xl font-black text-[#151827]">{t('landing.promise.cards.card_3_title')}</div><div className="mt-1 text-sm text-[#2a2f40]/70">{t('landing.promise.cards.card_3_body')}</div></div>
            <div className="rounded-xl border-4 border-[#151827] bg-white p-6" style={{ boxShadow: '6px 6px 0 #713d8d' }}><div className="text-2xl font-black text-[#713d8d]">{t('landing.promise.cards.card_4_title')}</div><div className="mt-1 text-sm text-[#2a2f40]/70">{t('landing.promise.cards.card_4_body')}</div></div>
          </div>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-12 text-3xl font-black md:text-4xl">{t('landing.why_needed.title')}</h2>

          <div className="space-y-8 text-lg text-[#2a2f40]/80 md:text-xl">
            <div>
              <p>{t('landing.why_needed.lines.line_1')}</p>
              <p>{t('landing.why_needed.lines.line_2')}</p>
            </div>

            <p className="font-semibold text-[#151827]">{t('landing.why_needed.lines.line_3')}</p>

            <div>
              <p>{t('landing.why_needed.lines.line_4')}</p>
              <p className="font-semibold text-[#151827]">{t('landing.why_needed.lines.line_5')}</p>
            </div>

            <div className="rounded-2xl border-y-4 border-[#151827] py-8">
              <p className="text-[#3f7f65]">{t('landing.why_needed.lines.line_6')}</p>
              <p className="text-[#713d8d]">{t('landing.why_needed.lines.line_7')}</p>
              <p className="font-semibold text-[#151827]">{t('landing.why_needed.lines.line_8')}</p>
            </div>

            <p className="text-xl font-black text-[#151827] md:text-2xl">
              {t('landing.why_needed.outro_prefix')}
              <br />
              <span className="text-[#3f7f65]">{t('landing.why_needed.outro_accent')}</span>
            </p>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-gradient-to-r from-[#3f7f65] to-[#713d8d] py-20 text-white md:py-32">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-8 text-3xl font-black md:text-5xl">{t('landing.final_cta.title')}</h2>

          <p className="mb-12 text-xl text-green-100 md:text-2xl">
            {t('landing.final_cta.body_1')}
            <br />
            <span className="font-semibold text-white">{t('landing.final_cta.body_2')}</span>
          </p>

          <div className="flex justify-center">
            <button
              onClick={() => handleCTAClick(talentUrl, 'final-cta')}
              className="rounded-xl bg-white px-8 py-4 text-lg font-black text-[#3f7f65]"
            >
              {t('landing.final_cta.button')}
            </button>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t-4 border-[#151827] bg-[#151827] py-10 text-white">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-5 sm:px-8">
          <div className="text-xl font-black uppercase tracking-tight">{t('landing.footer.tagline')}</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/75">
            <button onClick={() => onNavigate(talentUrl)} className="transition hover:text-white">{t('landing.footer.for_talent')}</button>
            <button onClick={() => onNavigate(bedrijvenUrl)} className="transition hover:text-white">{t('landing.footer.company_brand')}</button>
            <button onClick={() => onNavigate(werepresentUrl)} className="transition hover:text-white">WeRepresent</button>
            <a href="mailto:info@betaaldsolliciteren.nl" className="transition hover:text-white">{t('landing.footer.contact')}</a>
            <button onClick={() => onNavigate(privacyUrl)} className="transition hover:text-white">{t('landing.footer.privacy')}</button>
            <button onClick={() => onNavigate(voorwaardenUrl)} className="transition hover:text-white">{t('landing.footer.terms')}</button>
            <button onClick={() => onNavigate(aiPolicyUrl)} className="transition hover:text-white">AI Policy</button>
            <button onClick={() => onNavigate(cookieUrl)} className="transition hover:text-white">{t('landing.footer.cookies')}</button>
          </div>
          <div className="pt-4 text-sm text-white/55">© {new Date().getFullYear()} Betaaldsolliciteren.nl</div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }

        @keyframes dada-in {
          from {
            opacity: 0;
            transform: translateY(18px) rotate(-0.5deg);
          }
          to {
            opacity: 1;
            transform: translateY(0) rotate(0deg);
          }
        }

        .animate-marquee {
          animation: marquee 18s linear infinite;
        }

        .animate-dada-in {
          animation: dada-in 650ms ease-out both;
        }

        .animate-dada-in-delayed {
          animation: dada-in 800ms ease-out both;
          animation-delay: 80ms;
        }
      `}</style>
    </main>
  );
}